<template>
  <NrModal
    :primaryButtonText="itemModalText"
    secondaryButtonText="Cancel"
    :title="itemModalText"
    hasSecondaryButton
    :disableButtons="disableAddLineItemSubmit"
    :show="activeAddItemModal"
    @on-primary-click="addItem"
    @on-secondary-click="closeAddItemModal"
    @on-close-click="closeAddItemModal"
  >
    <NrSelect
      v-model="lineItemType"
      :options="itemOptions"
      :selectable="setDisabledLineItemTypes"
      :clearable="!invoiceItemToEdit"
      placeholder="Select line item type"
      appendToBody
    >
      <template v-slot:label>
        <span>Type</span>
      </template>
    </NrSelect>
    <NrSelect
      v-if="activeLineItemFields"
      v-model="selectedLineItem"
      :options="lineItemOptions"
      :selectable="setDisabledLineItems"
      placeholder="Select an item"
      @option:selected="addLineItem"
      appendToBody
    >
      <template v-slot:label>
        <span>Item</span>
      </template>
    </NrSelect>
    <div class="invoice-selected-item-description">
      {{ invoiceLineItemForm.selectedLineItem?.description }}
    </div>
    <NrSelect
      v-if="activeSurchargeFields"
      v-model="selectedSurchargeItem"
      :options="surchargeItemOptions"
      :selectable="setDisabledSurchargeItems"
      placeholder="Select an item"
      appendToBody
    >
      <template v-slot:label>
        <span>Fee or discount</span>
      </template>
    </NrSelect>
    <div class="invoice-selected-item-description">
      {{ selectedSurchargeItem?.description }}
    </div>
    <NrFormInput
      v-if="activeLineItemFields"
      v-model.number="lineItemQuantity"
      @input="roundQuantity"
      label="Quantity"
      type="number"
      placeholder="Add quantity"
      required
    />
    <div
      v-if="activeLineItemFields && invoiceLineItemForm.selectedLineItem?.label"
      class="invoice-totals-table"
    >
      <table>
        <thead>
          <tr class="invoice-cost-tr">
            <th scope="col">Name</th>
            <th scope="col">Cost per unit</th>
            <th scope="col">Quantity</th>
            <th class="align-right" scope="col">Total cost</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-if="invoiceLineItemForm.selectedLineItem.label?.length > 30">
              <NrTooltip :label="invoiceLineItemForm.selectedLineItem?.label">
                {{
                  truncate(invoiceLineItemForm.selectedLineItem?.label, {
                    length: 30
                  })
                }}
              </NrTooltip>
            </td>
            <td v-else>
              {{ invoiceLineItemForm.selectedLineItem?.label }}
            </td>
            <td>
              {{
                formatCost(invoiceLineItemForm.selectedLineItem?.cost, 4) ||
                '$0.00'
              }}
            </td>
            <td>{{ invoiceLineItemForm.lineItemQuantity }}</td>
            <td class="align-right">
              {{ formatCost(calculateLineItemCost) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="activeSurchargeFields && selectedSurchargeItem?.label"
      class="invoice-totals-table"
    >
      <table>
        <thead>
          <tr class="invoice-cost-tr">
            <th scope="col">Name</th>
            <th scope="col">{{ surchargeCostType }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-if="selectedSurchargeItem.label?.length > 30">
              <NrTooltip :label="selectedSurchargeItem?.label">
                {{ truncate(selectedSurchargeItem?.label, { length: 30 }) }}
              </NrTooltip>
            </td>
            <td v-else>
              {{ selectedSurchargeItem?.label }}
            </td>
            <td>
              <NrFormInput
                v-if="selectedSurchargeItem?.cost_type == 'custom'"
                name="Custom charge"
                type="number"
                class="invoice-cost-custom-amount"
                hideLabel
                placeholder="Input dollar amount"
                v-model="customSurchargeAmount"
              />
              <div v-else>
                {{ surchargeAmount }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </NrModal>
</template>

<script lang="ts">
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import API from '../../../../../vue_shared/api.js'
import truncate from 'lodash/truncate'
import { mapState, mapMutations, mapActions } from 'vuex'
import {
  SelectedLineItemOption,
  SurchargeOption,
  LineItemOption,
  ApiSurchargeItemResponse,
  ApiLineItemResponse
} from '../types/invoice_line_items'
import helpers from '../../../../../utils/helpers.js'

export default {
  components: {
    NrModal,
    NrTooltip,
    NrSelect,
    NrFormInput
  },

  data() {
    return {
      itemOptions: ['Line item', 'Fee or discount'],
      lineItemOptions: [],
      surchargeItemOptions: []
    }
  },

  watch: {
    activeAddItemModal(newVal, _): void {
      if (newVal) {
        this.fetchAndSetLineItems()
      }
    }
  },

  computed: {
    ...mapState('request_show', [
      'request',
      'invoiceItemToEdit',
      'invoiceLineItemForm',
      'invoiceLineItemType',
      'activeLineItemFields',
      'activeAddItemModal',
      'totalSelectedLineItems',
      'totalInvoiceAmount',
      'subtotalInvoiceAmount',
      'invoiceSelectedSurchargeItem',
      'totalSelectedSurchargeItems',
      'requestStaffCostUnpaid'
    ]),

    lineItemType: {
      get() {
        return this.invoiceLineItemType
      },
      set(val) {
        this.setInvoiceLineItemType(val)
      }
    },

    selectedLineItem: {
      get() {
        return this.invoiceLineItemForm.selectedLineItem
      },
      set(val) {
        this.setInvoiceSelectedLineItem(val)
      }
    },

    selectedSurchargeItem: {
      get() {
        return this.invoiceSelectedSurchargeItem
      },
      set(val) {
        this.setInvoiceSelectedSurchargeItem(val)
      }
    },

    lineItemQuantity: {
      get() {
        return this.invoiceLineItemForm.lineItemQuantity
      },
      set(val) {
        return this.setInvoiceLineItemQuantity(val)
      }
    },

    customSurchargeAmount: {
      get() {
        return this.selectedSurchargeItem.amount
      },
      set(val) {
        this.selectedSurchargeItem.amount = val
        this.setInvoiceSelectedSurchargeItem(this.selectedSurchargeItem)
      }
    },

    surchargeCostType(): string {
      if (this.selectedSurchargeItem?.cost_type === 'percentage') {
        return 'Percentage'
      } else {
        return 'Amount'
      }
    },

    itemModalText(): string {
      return this.invoiceItemToEdit ? 'Update item' : 'Add item'
    },

    disableAddLineItemSubmit(): boolean {
      if (this.activeLineItemFields) {
        return (
          !this.invoiceLineItemForm.lineItemQuantity ||
          !this.invoiceLineItemType ||
          !this.invoiceLineItemForm.selectedLineItem
        )
      } else {
        return !this.selectedSurchargeItem || !this.invoiceLineItemType
      }
    },

    activeLineItemFields(): boolean {
      return this.invoiceLineItemType === 'Line item'
    },

    activeSurchargeFields(): boolean {
      return this.invoiceLineItemType === 'Fee or discount'
    },

    calculateLineItemCost(): number {
      const cost =
        this.invoiceLineItemForm.lineItemQuantity *
        this.invoiceLineItemForm.selectedLineItem.cost

      // rounding total cost (quantity * cost) of exact item to cents (2 decimal points)
      return Math.round((cost + Number.EPSILON) * 100) / 100
    },

    surchargeAmount(): string {
      const amount = this.selectedSurchargeItem?.amount
      return this.surchargeCostType === 'Amount'
        ? this.formatCost(amount)
        : `${amount}%`
    }
  },

  methods: {
    ...mapActions('request_show', ['recalculateAllCosts']),

    ...mapMutations('request_show', [
      'setInvoiceAddItemModal',
      'setTotalSelectedLineItems',
      'setSubtotalInvoiceAmount',
      'setTotalInvoiceAmount',
      'setInvoiceItemToEdit',
      'setInvoiceLineItemType',
      'setInvoiceSelectedSurchargeItem',
      'setInvoiceSelectedLineItem',
      'setSurchargeItemOptions',
      'setInvoiceLineItemQuantity',
      'setTotalSelectedSurchargeItems'
    ]),

    truncate,

    formatCost(amount: number, maximumFractionDigits: number = 2) {
      return helpers.formatCost(amount, maximumFractionDigits)
    },

    addItem(): void {
      this.activeLineItemFields ? this.setLineItem() : this.setSurchargeItem()
      this.resetLineItemState()
      this.setInvoiceAddItemModal(false)
    },

    setLineItem(): void {
      // Check if item is already selected. If so, update existing item.
      const itemAlreadySelected = this.totalSelectedLineItems.find(
        (item: SelectedLineItemOption) =>
          item.label === this.invoiceLineItemForm.selectedLineItem.label
      )
      if (itemAlreadySelected) {
        this.updateLineItem()
      } else {
        // Replace line item if editing an existing line item and then selecting a new item, otherwise add a completely new item to the list
        !itemAlreadySelected && this.invoiceItemToEdit
          ? this.replaceLineItem()
          : this.addNewLineItem()
      }
      // Recalculate all costs if a line item is added after a surcharge
      if (this.totalSelectedSurchargeItems.length) {
        this.recalculateAllCosts()
      }
    },

    addNewLineItem() {
      const totalCost = this.calculateLineItemCost
      const lineItems = [
        ...this.totalSelectedLineItems,
        {
          ...this.invoiceLineItemForm.selectedLineItem,
          total_count: this.invoiceLineItemForm.lineItemQuantity,
          total_cost: totalCost
        }
      ]
      this.setTotalSelectedLineItems(lineItems)
      this.setTotalInvoiceAmount(this.totalInvoiceAmount + totalCost)
      this.setSubtotalInvoiceAmount(this.subtotalInvoiceAmount + totalCost)
    },

    updateLineItem() {
      const lineItems = this.totalSelectedLineItems.map(
        (item: SelectedLineItemOption) => {
          if (item.label === this.invoiceLineItemForm.selectedLineItem.label) {
            return {
              ...this.invoiceLineItemForm.selectedLineItem,
              total_count: this.invoiceLineItemForm.lineItemQuantity,
              total_cost: this.calculateLineItemCost
            }
          }
          return item
        }
      )

      this.setTotalSelectedLineItems(lineItems)
      this.recalculateAllCosts()
    },

    replaceLineItem() {
      const totalLineItems = this.totalSelectedLineItems.filter(
        (_: SelectedLineItemOption, index: number) =>
          index !== this.invoiceItemToEdit?.index
      )
      const lineItems = [
        ...totalLineItems,
        {
          ...this.invoiceLineItemForm.selectedLineItem,
          total_count: this.invoiceLineItemForm.lineItemQuantity,
          total_cost: this.calculateLineItemCost
        }
      ]
      this.setTotalSelectedLineItems(lineItems)
      this.recalculateAllCosts()
    },

    setSurchargeItem(): void {
      if (this.invoiceItemToEdit) {
        const items = this.totalSelectedSurchargeItems.filter(
          (_: SurchargeOption, index: number) =>
            index !== this.invoiceItemToEdit?.index
        )
        const surchargeItems = [...items, { ...this.selectedSurchargeItem }]
        this.setTotalSelectedSurchargeItems(surchargeItems)
        this.recalculateAllCosts()
      } else {
        const surchargeItems = [
          ...this.totalSelectedSurchargeItems,
          { ...this.selectedSurchargeItem }
        ]
        this.setTotalSelectedSurchargeItems(surchargeItems)
        this.calculateSurcharge(this.selectedSurchargeItem)
      }
    },

    resetLineItemState(): void {
      this.setInvoiceLineItemType('')
      this.setInvoiceLineItemQuantity(1)
      this.setInvoiceSelectedSurchargeItem('')
      this.setInvoiceSelectedLineItem('')
    },

    calculateSurcharge(surcharge: SurchargeOption): void {
      if (surcharge.cost_type === 'percentage') {
        const totalAmount =
          this.totalInvoiceAmount +
          (parseFloat(surcharge?.amount) / 100) * this.subtotalInvoiceAmount
        this.setTotalInvoiceAmount(totalAmount)
      } else {
        this.setTotalInvoiceAmount(
          this.totalInvoiceAmount + Number(surcharge?.amount)
        )
      }
    },

    closeAddItemModal(): void {
      this.setInvoiceItemToEdit(null)
      this.setInvoiceAddItemModal(false)
    },

    setDisabledLineItems(option: LineItemOption): boolean {
      const lineItemIds = this.totalSelectedLineItems.map(
        (item: SelectedLineItemOption) => item.value
      )
      return !lineItemIds.includes(option.value)
    },

    setDisabledLineItemTypes(): boolean {
      return !this.invoiceItemToEdit
    },

    addLineItem(): void {
      this.setInvoiceLineItemQuantity(1) // Sets item quantity to one by default
    },

    setDisabledSurchargeItems(option: SurchargeOption): boolean {
      const surchargeItems = this.totalSelectedSurchargeItems.map(
        (item: SurchargeOption) => item.value
      )
      return !surchargeItems.includes(option.value)
    },

    roundQuantity(val: number): void {
      const roundedVal = Math.round(val)
      this.invoiceLineItemForm.lineItemQuantity = Math.abs(roundedVal)
    },

    async fetchAndSetLineItems(): Promise<void> {
      const response = await API.fetchInvoiceCharges()
      const surcharges = response?.data?.surcharge_templates
      const lineItems = response?.data?.line_items_templates
      this.setSurchargeItemOptions(surcharges)
      this.setLineItemOptions(lineItems)
    },

    setSurchargeItemOptions(surcharges: Array<ApiSurchargeItemResponse>): void {
      this.surchargeItemOptions = surcharges?.map(
        (surcharge: ApiSurchargeItemResponse) => {
          return {
            value: surcharge.id,
            label: surcharge.name,
            amount: surcharge.amount,
            cost_type: surcharge.cost_type,
            description: surcharge.description
          }
        }
      )
    },

    setLineItemOptions(lineItems: Array<ApiLineItemResponse>): void {
      this.lineItemOptions = lineItems?.map((lineItem: ApiLineItemResponse) => {
        return {
          value: lineItem.id,
          label: lineItem.name,
          cost: lineItem.cost,
          description: lineItem.description
        }
      })
    }
  }
}
</script>
